// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { menuSecondaryNavigationItemStyles } from "@store/Themes/common/mixins/menu";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { formGridProps } from "@store/Themes/common/props/form";
import setAdditionalAccountSettingsValues from "@store/Framework/Store/Greif/Pages/AccountSettings/Handlers/SetAdditionalAccountSettingsValues";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const iconSize = pxToRem(18);

const enum fields {
    manageLanguageVerbiage = "manageLanguageVerbiage",
}

const mapStateToProps = (state: ApplicationState) => ({
    languages: getLanguages(state),
    account: state.pages.accountSettings.editingAccount,
    accountSettingState: state.greif.pages.accountSettings,
    currentAccount: getCurrentAccountState(state).value,
});

const mapDispatchToProps = {
    setAdditionalAccountSettingsValues,
};

export interface LanguageMenuStyles {
    languageWrapper?: InjectableCss;
    languageSelect?: SelectPresentationProps;
    manageLanguageIndications?: TypographyProps;
    manageLanguageTitle?: TypographyProps;
}

export const languageMenuStyles: LanguageMenuStyles = {
    manageLanguageTitle: formGridProps.title,
    manageLanguageIndications: {
        as: "p",
        css: css`
            font-size: ${x => x.theme.design.typography.size.small};
            padding-bottom: ${pxToRem(5)};
        `,
    },
    languageWrapper: {
        css: css`
            ${menuSecondaryNavigationItemStyles()}
            display: flex;
            align-items: center;
            width: 50%;
            ${breakpointValues(["100%", "48%"], "width")}
            &:hover {
                color: ${getColor("menu.secondaryNavigation.hover")};
                text-decoration: underline;
                transition: ${transitionHoverStyles()};
            }
            &&&& {
                color: black;
            }
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.manageLanguageVerbiage]: string;
    };
}

export const LogoImage = styled.img`
    width: ${iconSize};
    height: ${iconSize};
    background: currentColor;
    position: absolute;
    border-radius: 50%;
    object-fit: cover;
    overflow: hidden;
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
export const LanguageMenu: FC<Props> = ({
    languages,
    fields,
    account,
    setAdditionalAccountSettingsValues,
    accountSettingState,
    currentAccount,
}) => {
    const [accountLanguage, setAccountLanguage] = useState(languages?.find(x => x.isDefault)?.id || "");
    const styles = useMergeStyles("languageMenu", languageMenuStyles);

    useEffect(() => {
        setAccountLanguage(currentAccount?.properties?.communicationLanguageId || "");
        setAdditionalAccountSettingsValues({
            ...accountSettingState,
            language: currentAccount?.properties?.communicationLanguageId,
        });
    }, [account, currentAccount]);

    if (!languages || languages.length <= 1) {
        return null;
    }

    const menuId = "languageMenu";

    const updateAccountLanguage = (accountLanguage: string) => {
        setAccountLanguage(accountLanguage);
        setAdditionalAccountSettingsValues({ ...accountSettingState, language: accountLanguage });
    };

    return (
        <>
            <Typography {...styles.manageLanguageTitle}>{translate("Manage Communication Language")}</Typography>
            <Typography {...styles.manageLanguageIndications}>{fields.manageLanguageVerbiage}</Typography>
            <StyledWrapper {...styles.languageWrapper}>
                {accountLanguage ? (
                    <>
                        <Select
                            {...styles.languageSelect}
                            uid={menuId}
                            data-test-selector="languageSelector"
                            onChange={event => updateAccountLanguage(event.target.value)}
                            value={accountLanguage}
                            hasLabel
                        >
                            {languages.map(c => (
                                <option value={c.id} key={c.id}>
                                    {c.description}
                                </option>
                            ))}
                        </Select>
                    </>
                ) : (
                    <LoadingOverlay />
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LanguageMenu),
    definition: {
        displayName: "Prefered Language Menu",
        icon: "language",
        fieldDefinitions: [
            {
                name: fields.manageLanguageVerbiage,
                displayName: "Edit instructions (under title text)",
                editorTemplate: "TextField",
                defaultValue: "Please select the language used for our communication with you.",
                fieldType: "Translatable",
                sortOrder: 1,
            },
        ],
        group: "Account Settings",
        allowedContexts: ["AccountSettingsPage"],
    },
};

export default widgetModule;
